import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { CircularProgress, IconButton, IconButtonProps } from '@mui/material';

type PlaybackIconBtnV2Size = 'small' | 'medium' | 'large';

/** Icon shown while playing: pause resumes later; stop resets to the start. */
export type PlaybackIconBtnV2PlayingAction = 'pause' | 'stop';

const sizeStyles: Record<
  PlaybackIconBtnV2Size,
  {
    button: number;
    /** Explicit icon px so large stays ~60% of the button (MUI `large` is oversized at 35px). */
    iconPx: number;
    progress: number;
  }
> = {
  small: { button: 32, iconPx: 20, progress: 18 },
  medium: { button: 40, iconPx: 24, progress: 22 },
  large: { button: 48, iconPx: 28, progress: 28 },
};

export interface PlaybackIconBtnV2Props extends Omit<
  IconButtonProps,
  'children' | 'color' | 'size'
> {
  /** Whether playback is active (shows pause or stop affordance). */
  readonly isPlaying: boolean;
  /**
   * When true, shows a spinner instead of play/pause/stop (e.g. while audio seeks/loads).
   * Defaults to false so existing callers are unchanged.
   */
  readonly isLoading?: boolean;
  /**
   * Affordance while playing: `pause` (default) or `stop`.
   * Only affects the icon; the click handler decides the actual behavior.
   */
  readonly playingAction?: PlaybackIconBtnV2PlayingAction;
  /** Visual size of the circular control. Defaults to medium. */
  readonly size?: PlaybackIconBtnV2Size;
}

/**
 * Play / pause-or-stop icon button with a filled circular style when playing.
 */
export function PlaybackIconBtnV2({
  isPlaying,
  isLoading = false,
  playingAction = 'pause',
  size = 'medium',
  disabled,
  sx,
  ...props
}: PlaybackIconBtnV2Props) {
  const { button, iconPx, progress } = sizeStyles[size];
  const showPlaying = isPlaying && !isLoading;
  const iconSx = { fontSize: iconPx, display: 'block' };

  return (
    <IconButton
      {...props}
      size={size}
      disabled={isLoading || disabled}
      sx={[
        {
          width: button,
          height: button,
          p: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: showPlaying ? 'primary.main' : 'action.hover',
          color: showPlaying ? 'primary.contrastText' : 'text.primary',
          transition: (theme) =>
            theme.transitions.create(['background-color', 'color', 'box-shadow'], {
              duration: theme.transitions.duration.shorter,
            }),
          '&:hover': {
            bgcolor: showPlaying ? 'primary.dark' : 'action.selected',
          },
          '&.Mui-disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled',
          },
          // Optical nudge: play-triangle mass sits slightly left of geometric center.
          '& .PlaybackIconBtnV2-playIcon': {
            display: 'block',
            position: 'relative',
            left: '0px',
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {isLoading ? (
        <CircularProgress size={progress} color="inherit" />
      ) : isPlaying ? (
        playingAction === 'stop' ? (
          <StopRoundedIcon sx={iconSx} />
        ) : (
          <PauseRoundedIcon sx={iconSx} />
        )
      ) : (
        <PlayArrowRoundedIcon className="PlaybackIconBtnV2-playIcon" sx={iconSx} />
      )}
    </IconButton>
  );
}
